import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  Divider,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#0c0d12', paper: '#14171f' },
    text: {
      primary: '#e8e9ee',
      secondary: 'rgba(232,233,238,0.7)',
    },
    primary: { main: '#6aa9ff' },
  },
  typography: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
});

const tableHeadCellSx = {
  fontWeight: 700,
  color: '#d9e6ff',
  backgroundColor: 'rgba(30, 35, 52, 0.92)',
  borderBottom: '1px solid rgba(255,255,255,0.12)',
  textAlign: 'center',
};

const DataTable = () => {
  const [smsLogs, setSmsLogs] = useState([]);
  const [smsCounts, setSmsCounts] = useState({ g1: 0, g2: 0, g3: 0, g4: 0, g5: 0, g6: 0, g7: 0, g8: 0, g9: 0 });
  const [smsEarnings, setSmsEarnings] = useState({ g1: 0, g2: 0, g3: 0, g4: 0, g5: 0, g6: 0, g7: 0, g8: 0, g9: 0 });
  const [smsBilledSegments, setSmsBilledSegments] = useState({ g1: 0, g2: 0, g3: 0, g4: 0, g5: 0, g6: 0, g7: 0, g8: 0, g9: 0 });
  const [selectedsmsGateway, setSelectedsmsGateway] = useState('All');

  useEffect(() => {
    const fetchSmsLogs = async () => {
      const gateways = ['g1', 'g2', 'g3', 'g4', 'g5', 'g6', 'g7', 'g8', 'g9'];
      let allLogs = [];
      let counts = { g1: 0, g2: 0, g3: 0, g4: 0, g5: 0, g6: 0, g7: 0, g8: 0, g9: 0 };
      let earnings = { g1: 0, g2: 0, g3: 0, g4: 0, g5: 0, g6: 0, g7: 0, g8: 0, g9: 0 };
      let segments = { g1: 0, g2: 0, g3: 0, g4: 0, g5: 0, g6: 0, g7: 0, g8: 0, g9: 0 };

      for (const gateway of gateways) {
        try {
          const response = await axios.get(`https://frek.cc/proxy/38.54.86.24:5006/logs/sms/${gateway}`);
          const gatewayLogs = response.data.map((log) => ({ ...log, Gateway: gateway }));
          allLogs = allLogs.concat(gatewayLogs);

          counts[gateway] = gatewayLogs.length;
          earnings[gateway] = gatewayLogs.reduce((total, log) => {
            const seg = log.Segments || 1;
            segments[gateway] += seg;
            return total + seg * 0.0011;
          }, 0);
        } catch (error) {
          console.error(`Error fetching SMS logs from ${gateway}:`, error);
        }
      }

      setSmsLogs(allLogs);
      setSmsCounts(counts);
      setSmsEarnings(earnings);
      setSmsBilledSegments(segments);
    };

    fetchSmsLogs();
    const interval = setInterval(fetchSmsLogs, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.title = 'SMS Sent Logs';
  }, []);

  const filteredSmsLogs = selectedsmsGateway === 'All'
    ? smsLogs
    : smsLogs.filter((log) => log.Gateway === selectedsmsGateway);

  const totalSmsCount = Object.values(smsCounts).reduce((sum, count) => sum + count, 0);
  const totalSmsEarnings = Object.values(smsEarnings).reduce((sum, earnings) => sum + earnings, 0);
  const totalBilledSegments = Object.values(smsBilledSegments).reduce((a, b) => a + b, 0);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'radial-gradient(1200px 600px at 15% -10%, rgba(78, 118, 255, 0.28), transparent 60%),' +
            'radial-gradient(900px 600px at 110% 10%, rgba(0, 200, 170, 0.22), transparent 55%),' +
            'linear-gradient(180deg, #0b0b10 0%, #101018 45%, #0d0f12 100%)',
          color: '#e8e9ee',
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <Stack spacing={1.2}>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                  SMS Sent Logs
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip label={`Sent ${totalSmsCount.toLocaleString()}`} sx={{ bgcolor: 'rgba(106,169,255,0.2)', color: '#d5e7ff', fontWeight: 700 }} />
                  <Chip label={`Billed ${totalBilledSegments.toLocaleString()}`} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#e8e9ee', fontWeight: 700 }} />
                  <Chip label={`Bill EUR ${totalSmsEarnings.toFixed(3)}`} sx={{ bgcolor: 'rgba(90,210,122,0.18)', color: '#b9f2c8', fontWeight: 700 }} />
                </Stack>
              </Stack>
            </Paper>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
              <Paper
                elevation={0}
                sx={{
                  width: { xs: '100%', lg: 420 },
                  borderRadius: 3,
                  background: 'rgba(18, 18, 22, 0.75)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>Hourly SMS Data</Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>Goip</TableCell>
                        <TableCell sx={tableHeadCellSx}>Sent</TableCell>
                        <TableCell sx={tableHeadCellSx}>Billed</TableCell>
                        <TableCell sx={tableHeadCellSx}>Bill</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(smsCounts).map(([gateway, count]) => (
                        <TableRow key={gateway} sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
                          <TableCell align="center">{gateway}</TableCell>
                          <TableCell align="center">{count.toLocaleString()}</TableCell>
                          <TableCell align="center">{smsBilledSegments[gateway].toLocaleString()}</TableCell>
                          <TableCell align="center">EUR {smsEarnings[gateway].toFixed(3)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)', fontWeight: 700 } }}>
                        <TableCell align="center">Total</TableCell>
                        <TableCell align="center">{totalSmsCount.toLocaleString()}</TableCell>
                        <TableCell align="center">{totalBilledSegments.toLocaleString()}</TableCell>
                        <TableCell align="center">EUR {totalSmsEarnings.toFixed(3)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  borderRadius: 3,
                  background: 'rgba(18, 18, 22, 0.75)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>SMS Sent Realtime Logs</Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={selectedsmsGateway}
                      onChange={(e) => setSelectedsmsGateway(e.target.value)}
                      sx={{
                        height: 34,
                        backgroundColor: 'rgba(255,255,255,0.03)',
                      }}
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="g1">Goip 1</MenuItem>
                      <MenuItem value="g2">Goip 2</MenuItem>
                      <MenuItem value="g3">Goip 3</MenuItem>
                      <MenuItem value="g4">Goip 4</MenuItem>
                      <MenuItem value="g5">Goip 5</MenuItem>
                      <MenuItem value="g6">Goip 6</MenuItem>
                      <MenuItem value="g7">Goip 7</MenuItem>
                      <MenuItem value="g8">Goip 8</MenuItem>
                      <MenuItem value="g9">Goip 9</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <TableContainer sx={{ maxHeight: '76vh' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>SMS</TableCell>
                        <TableCell sx={tableHeadCellSx}>Bill</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSmsLogs.slice(0, 10000).map((log, index) => (
                        <TableRow key={index} sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
                          <TableCell align="center" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{log.Data}</TableCell>
                          <TableCell align="center">{log.Segments || 1}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default DataTable;
